import React, { Component } from 'react';

import {
  getSessionStorage,
  setSessionStorage,
  deleteSessionStorage,
} from '@zola-helpers/client/dist/es/util/storage';

import { connect } from 'react-redux';
import { compose } from 'redux';

import { getCurrentUserLocalData, getCurrentUser } from '~/actions/vendorAccountActions';
import { AppDispatch, AppThunk, RootState } from '~/reducers';
import {
  getIsUserContextLoaded,
  getIsAuthenticated,
  getIsIdTokenValid,
  getHasStorefront,
} from '~/selectors/vendorAccountSelectors';

function getDisplayName<T>(WrappedComponent: React.ComponentType<T>): string {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

interface AuthenticateVendorProps {
  isUserContextLoaded: boolean;
  isAuthenticated: boolean;
  isIdTokenValid: boolean;
  hasStorefront: boolean;
  getCurrentVendorUser: () => AppThunk<Promise<void>>;
}

function authenticateVendor<Props>(WrappedComponent: React.ComponentType<Props>) {
  class AuthenticateVendor extends Component<Props & AuthenticateVendorProps> {
    componentDidMount() {
      const { isUserContextLoaded, isIdTokenValid, getCurrentVendorUser } = this.props;
      const hasCurrentUserLocalData = getCurrentUserLocalData() !== null;
      if (!hasCurrentUserLocalData || !isUserContextLoaded || !isIdTokenValid) {
        getCurrentVendorUser();
      }
    }

    pathRequiresStorefront() {
      return window.location.pathname !== '/inspire/vendors/account';
    }

    notLoggingOut() {
      return document.referrer.indexOf('/inspire/vendors') === -1;
    }

    render() {
      const { isUserContextLoaded, isAuthenticated, hasStorefront } = this.props;

      if (!isUserContextLoaded) {
        return null;
      }

      if (!isAuthenticated || (!hasStorefront && this.pathRequiresStorefront())) {
        if (!isAuthenticated && this.notLoggingOut()) {
          setSessionStorage('vendorLoginRedirectUrl', window.location.href);
          // @ts-expect-error
          window.location = '/inspire/vendors/login';
        } else {
          // @ts-expect-error
          window.location = '/inspire/vendors';
        }
        return null;
      }

      if (isAuthenticated && getSessionStorage('onboardVendorType')) {
        deleteSessionStorage('onboardVendorType');
      }

      if (isAuthenticated && getSessionStorage('vendorLoginRedirectUrl')) {
        deleteSessionStorage('vendorLoginRedirectUrl');
      }

      return <WrappedComponent {...this.props} />;
    }
  }

  (
    AuthenticateVendor as React.ComponentClass<Props & AuthenticateVendorProps>
  ).displayName = `AuthenticateVendor(${getDisplayName(WrappedComponent)})`;

  return AuthenticateVendor;
}

const mapStateToProps = (state: RootState) => ({
  isUserContextLoaded: getIsUserContextLoaded(state),
  isAuthenticated: getIsAuthenticated(state),
  isIdTokenValid: getIsIdTokenValid(state),
  hasStorefront: getHasStorefront(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getCurrentVendorUser: () => dispatch(getCurrentUser()),
});

export default compose(connect(mapStateToProps, mapDispatchToProps), authenticateVendor) as <Props>(
  WrappedComponent: React.ComponentType<Props>
) => React.ComponentType<Props>;
